import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  deleteListing as deleteListingApi,
  resubmitListing as resubmitListingApi,
} from '../../util/api';
import { isDeletedListing } from '../../util/listingDeletion';
import { updatedEntities, denormalisedEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { BANNER_LISTING_TYPE } from '../BannerAdminPage/BannerAdminPage.duck';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { parse } from '../../util/urlHelpers';

import { fetchCurrentUser } from '../../ducks/user.duck';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 42 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 42;

// ================ Selectors ================ //

/**
 * Get the denormalised own listing entities with the given IDs
 *
 * @param {Object} state the full Redux store
 * @param {Array<UUID>} listingIds listing IDs to select from the store
 */
export const getOwnListingsById = (state, listingIds) => {
  const { ownEntities } = state.ManageListingsPage;
  const resources = listingIds.map(id => ({
    id,
    type: 'ownListing',
  }));
  const throwIfNotFound = false;
  return denormalisedEntities(ownEntities, resources, throwIfNotFound);
};

// ================ Async Thunks ================ //

////////////////////////
// Query Own Listings //
////////////////////////
const queryOwnListingsPayloadCreator = (queryParams, { extra: sdk, dispatch, rejectWithValue }) => {
  const { perPage, ...rest } = queryParams;
  const params = { ...rest, perPage };

  return sdk.ownListings
    .query(params)
    .then(response => {
      dispatch(addOwnEntities(response));
      return response;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const queryOwnListingsThunk = createAsyncThunk(
  'app/ManageListingsPage/queryOwnListings',
  queryOwnListingsPayloadCreator
);
// Backward compatible wrapper for the thunk
export const queryOwnListings = queryParams => (dispatch, getState, sdk) => {
  return dispatch(queryOwnListingsThunk(queryParams)).unwrap();
};

///////////////////
// Close Listing //
///////////////////
const closeListingPayloadCreator = (listingId, { extra: sdk, rejectWithValue }) => {
  return sdk.ownListings
    .close({ id: listingId }, { expand: true })
    .then(response => response)
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const closeListingThunk = createAsyncThunk(
  'app/ManageListingsPage/closeListing',
  closeListingPayloadCreator
);
// Backward compatible wrapper for the thunk
export const closeListing = listingId => (dispatch, getState, sdk) => {
  return dispatch(closeListingThunk(listingId)).unwrap();
};

//////////////////
// Open Listing //
//////////////////
const openListingPayloadCreator = (listingId, { extra: sdk, rejectWithValue }) => {
  return sdk.ownListings
    .open({ id: listingId }, { expand: true })
    .then(response => response)
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const openListingThunk = createAsyncThunk(
  'app/ManageListingsPage/openListing',
  openListingPayloadCreator
);
// Backward compatible wrapper for the thunk
export const openListing = listingId => (dispatch, getState, sdk) => {
  return dispatch(openListingThunk(listingId)).unwrap();
};

/////////////////////
// Relist Listing  //
/////////////////////
// Stock-managed equivalent of "open listing": when a sold item drops
// to 0 stock the listing isn't closed, just out-of-stock. Putting it
// back on sale is a stock bump from 0 → 1 via Marketplace API's
// compareAndSet. The seller can adjust further via Pricing & stock.
const relistListingPayloadCreator = (listingId, { extra: sdk, rejectWithValue }) => {
  return sdk.stock
    .compareAndSet({ listingId, oldTotal: 0, newTotal: 1 }, { expand: true })
    .then(response => response)
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const relistListingThunk = createAsyncThunk(
  'app/ManageListingsPage/relistListing',
  relistListingPayloadCreator
);
// Backward compatible wrapper for the thunk
export const relistListing = listingId => dispatch => {
  return dispatch(relistListingThunk(listingId)).unwrap();
};

///////////////////
// Discard Draft //
///////////////////

const delay = ms => new Promise(resolve => window.setTimeout(resolve, ms));

const discardDraftPayloadCreator = (listingId, thunkAPI) => {
  const { getState, extra: sdk, dispatch, rejectWithValue } = thunkAPI;
  const { queryParams } = getState().ManageListingsPage;

  return sdk.ownListings
    .discardDraft({ id: listingId }, { expand: true })
    .then(() => {
      // Return the listing update with a delay, so that the user
      // notices which listing gets removed
      return Promise.all([delay(300), sdk.ownListings.query(queryParams)]);
    })
    .then(([_, listingResponse]) => {
      dispatch(addOwnEntities(listingResponse));
      return listingResponse;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const discardDraftThunk = createAsyncThunk(
  'app/ManageListingsPage/discardDraft',
  discardDraftPayloadCreator
);
// Backward compatible wrapper for the thunk
export const discardDraft = listingId => (dispatch, getState, sdk) => {
  return dispatch(discardDraftThunk(listingId)).unwrap();
};

/////////////////////////
// Resubmit Listing    //
/////////////////////////
// "Send inn på nytt" after an admin asked for changes. The seller cannot
// clear the rejection themselves (it lives in metadata), so our server does
// it; the listing is then re-read so the card drops back to "waiting for
// approval" without a page reload.
const resubmitListingPayloadCreator = (listingId, { extra: sdk, rejectWithValue }) => {
  return resubmitListingApi(listingId.uuid)
    .then(() => sdk.ownListings.show({ id: listingId }))
    .then(response => response)
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const resubmitListingThunk = createAsyncThunk(
  'app/ManageListingsPage/resubmitListing',
  resubmitListingPayloadCreator
);
// Backward compatible wrapper for the thunk
export const resubmitListing = listingId => dispatch => {
  return dispatch(resubmitListingThunk(listingId)).unwrap();
};

/////////////////////
// Delete Listing  //
/////////////////////
// "Slett annonse": our server marks the listing deleted and closes it (only
// it can write the metadata). The card then leaves the page.
const deleteListingPayloadCreator = ({ listingId, reason, reasonText }, { rejectWithValue }) => {
  return deleteListingApi(listingId.uuid, reason, reasonText)
    .then(response => ({ listingId, ...response }))
    .catch(e => rejectWithValue({ error: e?.error || 'delete-failed' }));
};

export const deleteListingThunk = createAsyncThunk(
  'app/ManageListingsPage/deleteListing',
  deleteListingPayloadCreator
);
// Resolves on success; rejects with { error } (e.g. 'active-order').
export const deleteListing = (listingId, reason, reasonText) => dispatch => {
  return dispatch(deleteListingThunk({ listingId, reason, reasonText })).unwrap();
};

// ================ Slice ================ //

// Homepage banners live as draft listings owned by an admin (see
// BannerAdminPage.duck). They are not for sale, so they never belong in
// "Dine annonser". The API has no "not this value" filter for publicData, so
// they are dropped from the result here instead of in the query — which means
// the page's total count can be a few high for an admin. Only an admin owns
// any, and only a handful, so that is the cheaper wrong than hardcoding the
// real listing types into the query and hiding anything without one.
const isBannerListing = l => l.attributes?.publicData?.listingType === BANNER_LISTING_TYPE;
// A listing its seller deleted is closed in Sharetribe, but gone for them: it
// is dropped the same way, so the page can show a few fewer than a full page.
const resultIds = data =>
  data.data.filter(l => !isBannerListing(l) && !isDeletedListing(l)).map(l => l.id);

const updateListingAttributes = (state, listingEntity) => {
  const oldListing = state.ownEntities.ownListing[listingEntity.id.uuid];
  const updatedListing = { ...oldListing, attributes: listingEntity.attributes };
  const ownListingEntities = {
    ...state.ownEntities.ownListing,
    [listingEntity.id.uuid]: updatedListing,
  };
  return {
    ...state,
    ownEntities: { ...state.ownEntities, ownListing: ownListingEntities },
  };
};

const manageListingsPageSlice = createSlice({
  name: 'ManageListingsPage',
  initialState: {
    pagination: null,
    queryParams: null,
    queryInProgress: false,
    queryListingsError: null,
    currentPageResultIds: [],
    ownEntities: {},
    openingListing: null,
    openingListingError: null,
    closingListing: null,
    closingListingError: null,
    relistingListing: null,
    relistingListingError: null,
    discardingDraft: null,
    discardingDraftError: null,
    resubmittingListing: null,
    resubmittingListingError: null,
    // uuids resubmitted during this visit, so their cards can confirm it
    resubmittedListingIds: [],
  },
  reducers: {
    clearOpenListingError: state => {
      state.openingListing = null;
      state.openingListingError = null;
    },
    addOwnEntities: (state, action) => {
      // This works the same way as addMarketplaceEntities,
      // but we don't want to mix own listings with searched listings
      // (own listings data contains different info - e.g. exact location etc.)
      const apiResponse = action.payload.data;
      state.ownEntities = updatedEntities({ ...state.ownEntities }, apiResponse);
    },
  },
  extraReducers: builder => {
    // Query own listings
    builder
      .addCase(queryOwnListingsThunk.pending, (state, action) => {
        state.queryParams = action.meta.arg;
        state.queryInProgress = true;
        state.queryListingsError = null;
        state.currentPageResultIds = [];
      })
      .addCase(queryOwnListingsThunk.fulfilled, (state, action) => {
        state.currentPageResultIds = resultIds(action.payload.data);
        state.pagination = action.payload.data.meta;
        state.queryInProgress = false;
      })
      .addCase(queryOwnListingsThunk.rejected, (state, action) => {
        console.error(action.payload || action.error);
        state.queryInProgress = false;
        state.queryListingsError = action.payload;
      });

    // Open listing
    builder
      .addCase(openListingThunk.pending, (state, action) => {
        state.openingListing = action.meta.arg;
        state.openingListingError = null;
      })
      .addCase(openListingThunk.fulfilled, (state, action) => {
        const listing = action.payload.data.data;
        const updatedState = updateListingAttributes(state, listing);
        state.ownEntities = updatedState.ownEntities;
        state.openingListing = null;
      })
      .addCase(openListingThunk.rejected, (state, action) => {
        console.error(action.payload || action.error);
        state.openingListingError = {
          listingId: state.openingListing,
          error: action.payload,
        };
        state.openingListing = null;
      });

    // Close listing
    builder
      .addCase(closeListingThunk.pending, (state, action) => {
        state.closingListing = action.meta.arg;
        state.closingListingError = null;
      })
      .addCase(closeListingThunk.fulfilled, (state, action) => {
        const listing = action.payload.data.data;
        const updatedState = updateListingAttributes(state, listing);
        state.ownEntities = updatedState.ownEntities;
        state.closingListing = null;
      })
      .addCase(closeListingThunk.rejected, (state, action) => {
        console.error(action.payload || action.error);
        state.closingListingError = {
          listingId: state.closingListing,
          error: action.payload,
        };
        state.closingListing = null;
      });

    // Relist listing — out-of-stock back to in-stock via compareAndSet.
    // Response payload's `data.data` is a `stock` resource, so we merge
    // it via the generic addOwnEntities path instead of the
    // listing-only updateListingAttributes helper.
    builder
      .addCase(relistListingThunk.pending, (state, action) => {
        state.relistingListing = action.meta.arg;
        state.relistingListingError = null;
      })
      .addCase(relistListingThunk.fulfilled, (state, action) => {
        const apiResponse = action.payload.data;
        state.ownEntities = updatedEntities({ ...state.ownEntities }, apiResponse);
        state.relistingListing = null;
      })
      .addCase(relistListingThunk.rejected, (state, action) => {
        console.error(action.payload || action.error);
        state.relistingListingError = {
          listingId: state.relistingListing,
          error: action.payload,
        };
        state.relistingListing = null;
      });

    // Discard draft
    builder
      .addCase(discardDraftThunk.pending, (state, action) => {
        state.discardingDraft = action.meta.arg;
        state.discardingDraftError = null;
      })
      .addCase(discardDraftThunk.fulfilled, (state, action) => {
        state.currentPageResultIds = resultIds(action.payload.data);
        state.pagination = action.payload.meta;
        state.discardingDraft = null;
      })
      .addCase(discardDraftThunk.rejected, (state, action) => {
        console.error(action.payload || action.error);
        state.discardingDraftError = {
          listingId: state.discardingDraft,
          error: action.payload,
        };
        state.discardingDraft = null;
      });

    // Delete listing
    builder.addCase(deleteListingThunk.fulfilled, (state, action) => {
      const { uuid } = action.payload.listingId;
      state.currentPageResultIds = state.currentPageResultIds.filter(id => id.uuid !== uuid);
      if (state.pagination?.totalItems > 0) {
        state.pagination.totalItems -= 1;
      }
    });

    // Resubmit listing
    builder
      .addCase(resubmitListingThunk.pending, (state, action) => {
        state.resubmittingListing = action.meta.arg;
        state.resubmittingListingError = null;
      })
      .addCase(resubmitListingThunk.fulfilled, (state, action) => {
        const listing = action.payload.data.data;
        const updatedState = updateListingAttributes(state, listing);
        state.ownEntities = updatedState.ownEntities;
        state.resubmittedListingIds.push(listing.id.uuid);
        state.resubmittingListing = null;
      })
      .addCase(resubmitListingThunk.rejected, (state, action) => {
        console.error(action.payload || action.error);
        state.resubmittingListingError = {
          listingId: state.resubmittingListing,
          error: action.payload,
        };
        state.resubmittingListing = null;
      });
  },
});

export const { clearOpenListingError, addOwnEntities } = manageListingsPageSlice.actions;
export default manageListingsPageSlice.reducer;

// ================ Load data ================ //

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const queryParams = parse(search);
  const page = queryParams.page || 1;
  dispatch(clearOpenListingError());

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return Promise.all([
    dispatch(fetchCurrentUser()),
    dispatch(
      queryOwnListings({
        ...queryParams,
        page,
        perPage: RESULT_PAGE_SIZE,
        include: ['images', 'currentStock'],
        // Uncropped variants — ListingCard prefers these so the card crop is
        // centred by CSS rather than imgix's off-centre edge detection.
        'fields.image': [
          'variants.scaled-small',
          'variants.scaled-medium',
          `variants.${variantPrefix}`,
          `variants.${variantPrefix}-2x`,
        ],
        ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
        ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
        'limit.images': 1,
      })
    ),
  ])
    .then(response => {
      // const currentUser = response[0]?.data?.data;
      const ownListings = response[1]?.data?.data;
      return ownListings;
    })
    .catch(e => {
      throw e;
    });
};
